import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ChefHat,
  CircleDot,
  ClipboardList,
  LayoutGrid,
  LogOut,
  Package,
  Settings,
  ShoppingBag,
  Sparkles,
  Truck,
  Users,
  Utensils,
  UtensilsCrossed,
  Wrench,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleProvider";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import BranchCounterSelectors from "@/components/layout/BranchCounterSelectors";
import ClockWeatherWidget from "@/components/dashboard/ClockWeatherWidget";
import {
  can,
  canAccessDashboard,
  canUseWaiterScreen,
  canViewKitchenOrders,
  isMultiBranchFeatureEnabled,
  isOnlineFeatureEnabled,
} from "@/lib/permissions";
import api from "@/lib/api";
import { useCurrency } from "@/hooks/useCurrency";
import { cn } from "@/lib/utils";
import "@/styles/simple-ui.css";

const TILES = [
  {
    id: "operations",
    to: "/operations",
    labelKey: "nav.operations",
    hintKey: "simple.tileOperationsHint",
    icon: Wrench,
    bg: "simple-tile-grad-ops",
    show: (p) =>
      can(p, "pos.view")
      || can(p, "reports.view")
      || can(p, "delivery.view")
      || canViewKitchenOrders(p)
      || canUseWaiterScreen(p),
  },
  {
    id: "pos",
    to: "/pos",
    labelKey: "nav.pointOfSale",
    hintKey: "simple.tilePosHint",
    icon: ShoppingBag,
    bg: "bg-[#F97316]",
    show: (p) => can(p, "pos.view"),
  },
  {
    id: "tables",
    to: "/tables",
    labelKey: "nav.tableManagement",
    hintKey: "simple.tileTablesHint",
    icon: LayoutGrid,
    bg: "bg-[#2563EB]",
    show: (p) => can(p, "pos.view"),
  },
  {
    id: "kitchen",
    to: "/kds",
    labelKey: "nav.kitchenOrders",
    hintKey: "simple.tileKitchenHint",
    icon: ChefHat,
    bg: "bg-[#059669]",
    show: (p) => canViewKitchenOrders(p),
  },
  {
    id: "delivery",
    to: "/delivery",
    labelKey: "nav.delivery",
    hintKey: "simple.tileDeliveryHint",
    icon: Truck,
    bg: "bg-[#0D9488]",
    show: (p) => can(p, "delivery.view"),
  },
  {
    id: "waiter",
    to: "/waiter",
    labelKey: "nav.waiterOrders",
    hintKey: "simple.tileWaiterHint",
    icon: Utensils,
    bg: "bg-[#7C3AED]",
    show: (p) => canUseWaiterScreen(p),
  },
  {
    id: "online",
    to: "/online/orders",
    labelKey: "nav.onlineOrders",
    hintKey: "simple.tileOnlineHint",
    icon: ClipboardList,
    bg: "bg-[#DC2626]",
    show: (p, user) => can(p, "online.orders.view") && isOnlineFeatureEnabled(user),
  },
  {
    id: "masters",
    to: "/masters",
    labelKey: "nav.masters",
    hintKey: "simple.tileMastersHint",
    icon: Package,
    bg: "bg-[#D97706]",
    show: (p) => can(p, "masters.view"),
  },
  {
    id: "reports",
    to: "/reports",
    labelKey: "nav.reports",
    hintKey: "simple.tileReportsHint",
    icon: ClipboardList,
    bg: "bg-[#4F46E5]",
    show: (p) => can(p, "reports.view"),
  },
  {
    id: "admin",
    to: "/admin",
    labelKey: "nav.administration",
    hintKey: "simple.tileAdminHint",
    icon: Settings,
    bg: "bg-[#475569]",
    show: (p) => can(p, "administration.view"),
  },
  {
    id: "users",
    to: "/admin/users",
    labelKey: "nav.users",
    hintKey: "simple.tileUsersHint",
    icon: Users,
    bg: "bg-[#9333EA]",
    show: (p) => can(p, "administration.view"),
  },
];

function LivePulse({ active }) {
  return (
    <span className="relative inline-flex h-2.5 w-2.5">
      {active ? (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-60" />
      ) : null}
      <span
        className={cn(
          "relative inline-flex h-2.5 w-2.5 rounded-full",
          active ? "bg-emerald-300" : "bg-white/50",
        )}
      />
    </span>
  );
}

export default function SimpleHome() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isRtl } = useLocale();
  const { user, logout } = useAuth();
  const { format: money } = useCurrency();
  const permissions = user?.permissions || [];
  const branchId = user?.default_branch?.id;
  const companyName = user?.company?.name || t("app.title");
  const canPos = can(permissions, "pos.view");
  const canReports = can(permissions, "reports.view");
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const showDashWidgets = canAccessDashboard(permissions) || canPos;
  const showNetSales = isAdmin && showDashWidgets;
  const showBranchBar = isMultiBranchFeatureEnabled(user);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const locale = i18n.language?.startsWith("ar") ? "ar-AE" : undefined;
  const timeStr = now.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  const dateStr = now.toLocaleDateString(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  const { data: businessDayPayload } = useQuery({
    queryKey: ["business-day", branchId],
    queryFn: async () => {
      const { data } = await api.get("/business-day/current", { params: { branch_id: branchId } });
      return data;
    },
    enabled: Boolean(branchId && (canPos || canReports)),
    staleTime: 15_000,
    refetchOnMount: "always",
  });
  const businessDay = businessDayPayload?.business_day;
  const dayIsOpen = businessDay?.status === "open";

  const { data: dash } = useQuery({
    queryKey: ["dashboard", branchId, "today"],
    enabled: Boolean(branchId && showDashWidgets),
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data: res } = await api.get("/dashboard", {
        params: { branch_id: branchId, period: "today" },
      });
      return res;
    },
  });
  const kpis = dash?.kpis;
  const live = dash?.live_status;

  const tiles = useMemo(
    () => TILES.filter((tile) => tile.show(permissions, user)),
    [permissions, user],
  );

  return (
    <div
      className="simple-ui simple-home flex h-full min-h-0 flex-col overflow-hidden"
      dir={isRtl ? "rtl" : "ltr"}
      data-testid="simple-home"
    >
      <header className="simple-header simple-home-header shrink-0 print:hidden">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/20 shadow-inner ring-1 ring-white/25 sm:h-14 sm:w-14">
              <UtensilsCrossed className="h-6 w-6 sm:h-7 sm:w-7" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-[18px] font-extrabold leading-tight tracking-tight sm:text-[22px]">
                  {companyName}
                </h1>
                <Sparkles className="hidden h-4 w-4 shrink-0 text-amber-200 sm:block" />
              </div>
              <p className="truncate text-[12px] text-white/80 sm:text-[13px]">
                {user?.name}
                {user?.default_branch?.name ? ` · ${user.default_branch.name}` : ""}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="hidden rounded-2xl bg-white/10 px-3 py-1.5 text-end ring-1 ring-white/15 md:block">
              <div className="font-mono text-[15px] font-bold tabular-nums leading-none" data-testid="simple-home-clock">
                {timeStr}
              </div>
              <div className="mt-0.5 text-[11px] text-white/75">{dateStr}</div>
            </div>
            <LanguageSwitcher className="h-11 border-white/30 bg-white/15 text-white hover:bg-white/25" />
            <button
              type="button"
              onClick={() => logout()}
              className="inline-flex h-11 min-w-[44px] items-center gap-1.5 rounded-xl bg-white/15 px-3 text-[13px] font-semibold hover:bg-white/25"
              data-testid="simple-logout-btn"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t("common.signOut")}</span>
            </button>
          </div>
        </div>

        {showBranchBar ? (
          <div className="border-t border-white/10 bg-black/10 px-4 py-2">
            <div className="mx-auto max-w-[1200px]">
              <BranchCounterSelectors className="!flex flex-wrap" showCounter={false} />
            </div>
          </div>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1200px] px-4 py-4 sm:py-6">
          {/* Equal-width status tiles: 4 when admin sales shown, else 3 */}
          <div
            className="simple-status-bar mb-3 sm:mb-4"
            data-testid="simple-home-widgets"
          >
            {(canPos || canReports) && branchId ? (
              <button
                type="button"
                onClick={() => navigate("/operations")}
                className={cn(
                  "simple-status-tab text-start",
                  dayIsOpen ? "simple-widget--open" : "simple-widget--closed",
                )}
                data-testid="simple-day-status-widget"
              >
                <span className="simple-status-tab__label">
                  <LivePulse active={dayIsOpen} />
                  {t("simple.dayStatusLabel", "Business day")}
                </span>
                <span className="simple-status-tab__value">
                  {dayIsOpen
                    ? t("simple.dayStatusOpenShort", "Open")
                    : t("simple.dayStatusClosed", "Day closed")}
                </span>
                <span className="simple-status-tab__meta">
                  {dayIsOpen
                    ? businessDay?.business_date || "—"
                    : t("simple.dayStatusTapOps", "Tap → Operations")}
                </span>
              </button>
            ) : null}

            {showNetSales ? (
              <div
                className="simple-status-tab simple-widget--sales"
                data-testid="simple-net-sales-tab"
              >
                <span className="simple-status-tab__label">
                  {t("dashboard.netSales", "Net sales")}
                </span>
                <span className="simple-status-tab__value tabular-nums">
                  {money(kpis?.net_sales ?? 0)}
                </span>
                <span className="simple-status-tab__meta">
                  {t("dashboard.orders", "Orders")}: {kpis?.bill_count ?? live?.open_orders ?? 0}
                </span>
              </div>
            ) : null}

            {showDashWidgets ? (
              <button
                type="button"
                onClick={() => navigate("/pos")}
                className="simple-status-tab simple-widget--live text-start"
                data-testid="simple-live-widget"
              >
                <span className="simple-status-tab__label">
                  <CircleDot className="h-3 w-3 text-orange-200" />
                  {t("dashboard.liveStatus", "Live")}
                </span>
                <span className="simple-status-tab__value simple-status-tab__value--split">
                  <span>
                    <strong>{live?.open_orders ?? 0}</strong>
                    <em>{t("dashboard.openOrders", "Open")}</em>
                  </span>
                  <span>
                    <strong>{live?.delivery_active ?? 0}</strong>
                    <em>{t("dashboard.deliveryOut", "Delivery")}</em>
                  </span>
                </span>
              </button>
            ) : null}

            <div className="simple-status-tab simple-status-glance">
              <ClockWeatherWidget
                variant="bar"
                className="!min-h-0 !rounded-[0.7rem] !shadow-none h-full w-full"
              />
            </div>
          </div>

          {tiles.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
              {t("simple.noModules")}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-4">
              {tiles.map((tile) => {
                const Icon = tile.icon;
                return (
                  <Link
                    key={tile.id}
                    to={tile.to}
                    className={cn(
                      "simple-tile flex min-h-[124px] flex-col items-center justify-center gap-2.5 p-4 text-center text-white sm:min-h-[140px] sm:gap-3",
                      tile.bg,
                    )}
                    data-testid={`simple-tile-${tile.id}`}
                  >
                    <span className="grid h-14 w-14 place-items-center rounded-2xl bg-white/20 sm:h-16 sm:w-16">
                      <Icon className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2.25} />
                    </span>
                    <span className="text-[15px] font-bold leading-tight sm:text-[17px]">
                      {t(tile.labelKey)}
                    </span>
                    <span className="text-[11px] font-medium text-white/85 sm:text-[12px]">
                      {t(tile.hintKey)}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
